import { EVProfileId, EVSoundProfile } from '../types';

export const EV_SOUND_PROFILES: Record<EVProfileId, EVSoundProfile> = {
  ev_expressive: {
    id: 'ev_expressive',
    name: 'Iconic Expressive',
    tagline: 'Cinematic THX swell resolving into glorious Major 9th chord',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 75,
    harmonicRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 6.0, 7.0],
    fundamentalGain: 0.95,
    harmonicGain: 1.05,
    padGain: 0.95,
    choirGain: 0.75,
    accelerationGain: 1.25,
    brakingGain: 0.85,
    airGain: 0.45,
    spatialWidth: 0.90,
    reverbAmount: 0.50,
    brightness: 0.85,
    accelerationSensitivity: 0.95,
    brakingSensitivity: 0.80,
    coastingReduction: 0.90, // Intelligently cuts drone on highway
    attackTime: 0.20,
    releaseTime: 0.45,
    attackTimeMs: 200,
    releaseTimeMs: 450,
    cinematicIntensity: 0.95,
    hasChordProgression: true,
    description: 'Inspired by Hans Zimmer’s iconic BMW soundscapes. Builds dramatic harmonic tension under acceleration, then resolves into a breathtaking THX-style Major 9th chord at steady cruise.',
  },

  ev_relax: {
    id: 'ev_relax',
    name: 'Iconic Relax',
    tagline: 'Soothing massage-like sanctuary with velvety sub-bass warmth',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 58, // Deep warm resting fundamental
    harmonicRatios: [1.0, 2.0, 3.0, 5.0],
    fundamentalGain: 1.15, // Rich, physical sub-bass body
    harmonicGain: 0.45,
    padGain: 1.20, // Velvety ambient pad
    choirGain: 0.35,
    accelerationGain: 0.50,
    brakingGain: 0.40,
    airGain: 0.25,
    spatialWidth: 0.70,
    reverbAmount: 0.55,
    brightness: 0.40, // Warm analog lowpass rolloff
    accelerationSensitivity: 0.45,
    brakingSensitivity: 0.45,
    coastingReduction: 0.95, // Near total silence when coasting
    attackTime: 0.50,
    releaseTime: 0.85,
    attackTimeMs: 500,
    releaseTimeMs: 850,
    cinematicIntensity: 0.50,
    description: 'Transform your cabin into a tranquil spa sanctuary. Soft acoustic sub-bass swells provide a gentle, massage-like physical sensation with zero harsh frequencies.',
  },

  ev_sport: {
    id: 'ev_sport',
    name: 'Iconic Sport',
    tagline: 'Aggressive electric hyper-drive with visceral acceleration bite',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 85,
    harmonicRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 7.0, 8.0],
    fundamentalGain: 1.10,
    harmonicGain: 1.30,
    padGain: 0.65,
    choirGain: 0.50,
    accelerationGain: 1.45,
    brakingGain: 1.15,
    airGain: 0.50,
    spatialWidth: 0.95,
    reverbAmount: 0.35,
    brightness: 0.95,
    accelerationSensitivity: 1.15,
    brakingSensitivity: 1.0,
    coastingReduction: 0.80,
    attackTime: 0.08,
    releaseTime: 0.25,
    attackTimeMs: 80,
    releaseTimeMs: 250,
    cinematicIntensity: 1.0,
    description: 'High-voltage electric hyper-drive. Immediate punchy attack under acceleration, dense harmonic saturation, and dramatic descending deceleration tone on lift-off.',
  },

  ev_cinematic: {
    id: 'ev_cinematic',
    name: 'Iconic Cinematic',
    tagline: 'Orchestral film-score soundscape with sweeping harmonic depth',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 70,
    harmonicRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 7.0],
    fundamentalGain: 1.0,
    harmonicGain: 0.85,
    padGain: 1.05,
    choirGain: 0.65,
    accelerationGain: 0.90,
    brakingGain: 0.75,
    airGain: 0.45,
    spatialWidth: 0.85,
    reverbAmount: 0.45,
    brightness: 0.75,
    accelerationSensitivity: 0.75,
    brakingSensitivity: 0.70,
    coastingReduction: 0.85,
    attackTime: 0.30,
    releaseTime: 0.60,
    attackTimeMs: 300,
    releaseTimeMs: 600,
    cinematicIntensity: 0.85,
    description: 'Rich orchestral synth textures that expand like a cinematic film score, dynamically shifting with vehicle speed and throttle input.',
  },

  ev_pulse: {
    id: 'ev_pulse',
    name: 'Iconic Pulse',
    tagline: 'Kinetic electronic drive with dynamic load-reactive heartbeat',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 72,
    harmonicRatios: [1.0, 2.0, 3.0, 4.0, 6.0],
    fundamentalGain: 1.0,
    harmonicGain: 0.95,
    padGain: 0.75,
    choirGain: 0.35,
    accelerationGain: 1.10,
    brakingGain: 0.85,
    airGain: 0.40,
    spatialWidth: 0.80,
    reverbAmount: 0.40,
    brightness: 0.80,
    accelerationSensitivity: 0.85,
    brakingSensitivity: 0.75,
    coastingReduction: 0.85,
    attackTime: 0.18,
    releaseTime: 0.40,
    attackTimeMs: 180,
    releaseTimeMs: 400,
    cinematicIntensity: 0.88,
    pulseBaseRate: 1.2,
    pulseAccelFactor: 3.5,
    description: 'Dynamic kinetic electronic pulse that quickens and intensifies as you demand more power, giving a visceral sense of electrical propulsion energy.',
  },

  ev_spatial: {
    id: 'ev_spatial',
    name: 'Iconic Spatial',
    tagline: 'Expansive 3D sound field with celestial synthetic choir',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 75,
    harmonicRatios: [1.0, 1.5, 2.0, 3.0, 4.0, 5.0, 7.0],
    fundamentalGain: 0.95,
    harmonicGain: 0.85,
    padGain: 1.05,
    choirGain: 1.30,
    airGain: 0.70,
    accelerationGain: 0.95,
    brakingGain: 0.75,
    spatialWidth: 1.0, // Full cabin envelopment
    reverbAmount: 0.70,
    brightness: 0.75,
    accelerationSensitivity: 0.70,
    brakingSensitivity: 0.65,
    coastingReduction: 0.85,
    attackTime: 0.30,
    releaseTime: 0.65,
    attackTimeMs: 300,
    releaseTimeMs: 650,
    cinematicIntensity: 0.90,
    description: 'An ethereal multi-voice harmonic choir field with sweeping acoustic formants that envelops the entire interior of the car.',
  },

  // Alias for backward compatibility
  ev_futuristic: {
    id: 'ev_futuristic',
    name: 'Iconic Expressive',
    tagline: 'Cinematic THX swell resolving into glorious Major 9th chord',
    category: 'EV',
    transmissionMode: 'NONE',
    baseFrequency: 75,
    harmonicRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 6.0, 7.0],
    fundamentalGain: 0.95,
    harmonicGain: 1.05,
    padGain: 0.95,
    choirGain: 0.75,
    accelerationGain: 1.25,
    brakingGain: 0.85,
    airGain: 0.45,
    spatialWidth: 0.90,
    reverbAmount: 0.50,
    brightness: 0.85,
    accelerationSensitivity: 0.95,
    brakingSensitivity: 0.80,
    coastingReduction: 0.90,
    attackTime: 0.20,
    releaseTime: 0.45,
    attackTimeMs: 200,
    releaseTimeMs: 450,
    cinematicIntensity: 0.95,
    hasChordProgression: true,
    description: 'Inspired by Hans Zimmer’s iconic BMW soundscapes. Builds dramatic harmonic tension under acceleration, then resolves into a breathtaking THX-style Major 9th chord at steady cruise.',
  },
};

export const DEFAULT_EV_PROFILE_ID: EVProfileId = 'ev_expressive';
